import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Field } from '@promtver/ui-kit';
import { EnterpriseStatus } from '@promtver/contracts';

import {
  getEnterprisesForModeration,
  publishEnterprise,
  rejectEnterprise,
  unpublishEnterprise,
} from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { Select } from '../../components/Select';
import { ru } from '../../i18n/ru';
import { categoryLabel } from '../../lib/format';

function statusLabel(status: EnterpriseStatus | string): string {
  switch (status) {
    case EnterpriseStatus.Draft:
      return ru.moderation.statusDraft;
    case EnterpriseStatus.OnReview:
      return ru.moderation.statusOnReview;
    case EnterpriseStatus.Published:
      return ru.moderation.statusPublished;
    case EnterpriseStatus.Rejected:
      return ru.moderation.statusRejected;
    case EnterpriseStatus.Archived:
      return ru.moderation.statusArchived;
    default:
      return ru.common.notSpecified;
  }
}

/**
 * Модерация карточек объектов (FR-ADM-02): приём, проверка, публикация,
 * снятие с публикации. Причина отклонения обязательна и фиксируется в журнале.
 */
export function ModerationPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>(EnterpriseStatus.OnReview);
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);

  const enterprisesQuery = useQuery({
    queryKey: ['admin', 'enterprises', status],
    queryFn: () => getEnterprisesForModeration(status ? (status as EnterpriseStatus) : undefined),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'enterprises'] });
  };

  const publishMutation = useMutation({
    mutationFn: (id: string) => publishEnterprise(id),
    onSuccess: async () => {
      setActionError(null);
      await invalidate();
    },
    onError: (error) => setActionError(errorMessage(error, ru.common.unknownError)),
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: string) => unpublishEnterprise(id),
    onSuccess: async () => {
      setActionError(null);
      await invalidate();
    },
    onError: (error) => setActionError(errorMessage(error, ru.common.unknownError)),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectEnterprise(id, reason),
    onSuccess: async () => {
      setActionError(null);
      await invalidate();
    },
    onError: (error) => setActionError(errorMessage(error, ru.common.unknownError)),
  });

  const enterprises = enterprisesQuery.data?.items ?? [];

  return (
    <AppShell title={ru.moderation.title} subtitle={ru.moderation.subtitle}>
      <Card title={ru.moderation.filterStatus}>
        <Select
          label={ru.moderation.filterStatus}
          value={status}
          onChange={setStatus}
          options={[
            { value: EnterpriseStatus.OnReview, label: ru.moderation.statusOnReview },
            { value: EnterpriseStatus.Published, label: ru.moderation.statusPublished },
            { value: EnterpriseStatus.Draft, label: ru.moderation.statusDraft },
            { value: EnterpriseStatus.Rejected, label: ru.moderation.statusRejected },
            { value: EnterpriseStatus.Archived, label: ru.moderation.statusArchived },
            { value: '', label: ru.common.all },
          ]}
        />
      </Card>

      {actionError ? (
        <p role="alert" className="text-xs text-danger">
          {actionError}
        </p>
      ) : null}

      <Card title={ru.moderation.object}>
        <AsyncState
          isLoading={enterprisesQuery.isLoading}
          isError={enterprisesQuery.isError}
          isEmpty={enterprises.length === 0}
          emptyTitle={ru.moderation.empty}
          onRetry={() => void enterprisesQuery.refetch()}
        >
          <ul className="flex flex-col gap-4">
            {enterprises.map((enterprise) => (
              <li key={enterprise.id} className="rounded-card border border-line p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <strong className="text-sm">{enterprise.name}</strong>
                  <Badge tone={enterprise.status === EnterpriseStatus.Published ? 'success' : 'warning'}>
                    {statusLabel(enterprise.status)}
                  </Badge>
                </div>

                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.common.name} value={enterprise.legalName} />
                  <KeyValue label={ru.common.district} value={enterprise.districtName ?? enterprise.districtId} />
                  <KeyValue label={ru.common.address} value={enterprise.address} />
                  <KeyValue label={ru.common.category} value={categoryLabel(enterprise.category)} />
                  <KeyValue label={ru.common.phone} value={enterprise.contactPhone ?? ru.common.notSpecified} mono />
                </div>

                <p className="mt-2 text-xs text-muted">{enterprise.description}</p>

                <div className="mt-3 flex flex-wrap gap-2">
                  {enterprise.status !== EnterpriseStatus.Published ? (
                    <Button
                      tone="success"
                      disabled={publishMutation.isPending}
                      onClick={() => publishMutation.mutate(enterprise.id)}
                    >
                      {ru.moderation.publish}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      tone="warning"
                      disabled={unpublishMutation.isPending}
                      onClick={() => unpublishMutation.mutate(enterprise.id)}
                    >
                      {ru.moderation.unpublish}
                    </Button>
                  )}
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  <Field
                    label={ru.moderation.rejectReason}
                    value={rejectReasons[enterprise.id] ?? ''}
                    onChange={(value) =>
                      setRejectReasons((previous) => ({ ...previous, [enterprise.id]: value }))
                    }
                    placeholder={ru.moderation.rejectHint}
                  />
                  <Button
                    variant="outline"
                    tone="danger"
                    disabled={
                      rejectMutation.isPending || (rejectReasons[enterprise.id] ?? '').trim().length === 0
                    }
                    onClick={() =>
                      rejectMutation.mutate({
                        id: enterprise.id,
                        reason: (rejectReasons[enterprise.id] ?? '').trim(),
                      })
                    }
                  >
                    {ru.moderation.reject}
                  </Button>
                  <p className="text-xs text-muted">{ru.moderation.rejectHint}</p>
                </div>
              </li>
            ))}
          </ul>
        </AsyncState>
      </Card>
    </AppShell>
  );
}
